// 参加者の検索ログ閲覧 (spec §4.7 ホスト側)。
// 「どの用語が分かりにくかったか」のシグナルとして使う。

import {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
} from 'react';
import { t, type Locale } from '@solo-webinar-copilot/i18n';
import type { SearchQuery } from '@solo-webinar-copilot/shared';
import { fetchSearchLog } from '../lib/api-client.ts';
import type { AppContext } from '../lib/zoom-sdk.ts';

const POLL_INTERVAL_MS = 5000;

export interface SearchLogProps {
  ctx: AppContext;
  locale: Locale;
}

export function SearchLog({ ctx, locale }: SearchLogProps): ReactElement {
  const [items, setItems] = useState<SearchQuery[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const r = await fetchSearchLog(ctx);
    if (r.ok) {
      setItems(r.items);
      setError(null);
    } else {
      setError(r.error);
    }
  }, [ctx]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      if (cancelled) return;
      await refresh();
      if (!cancelled) timer = setTimeout(tick, POLL_INTERVAL_MS);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [refresh]);

  return (
    <section data-testid="search-log">
      <h2>{t('in_client.host.search_log.title', locale)}</h2>
      {error ? (
        <p role="alert" data-testid="search-log-error">
          {t('in_client.error.network', locale)}: {error}
        </p>
      ) : null}
      {items.length === 0 ? (
        <p data-testid="search-log-empty">
          {t('in_client.host.search_log.empty', locale)}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {items.map((q) => (
            <li
              key={q.id}
              data-testid={`search-log-item-${q.id}`}
              style={{ marginBottom: 6, fontSize: 14 }}
            >
              <div>
                <strong>Q:</strong> {q.query}
                {q.shared_to_all === 1 ? (
                  <small
                    data-testid={`search-log-shared-${q.id}`}
                    style={{ marginLeft: 6, opacity: 0.6 }}
                  >
                    [{t('in_client.host.search_log.shared', locale)}]
                  </small>
                ) : null}
              </div>
              {q.ai_response ? (
                <div style={{ opacity: 0.7 }}>
                  <small>
                    [{t('in_client.ai.generated_label', locale)}]
                  </small>{' '}
                  {q.ai_response}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
